/**
 * "Players online now" via Supabase Realtime Presence.
 *
 * Every running client joins one global channel; the live count = number of distinct
 * present users (keyed by user id, so multiple tabs of the same account count once).
 * No database table needed. Note the free-tier ceiling is 200 concurrent connections —
 * past that, switch to a Durable Object counter or a DB "active in last N minutes" query.
 */
import { supabase } from "./supabase";
import type { Account } from "./auth";

/** Join the global presence channel; `onCount` fires with the live count. Returns a leave fn. */
export function joinOnlinePresence(account: Account, onCount: (n: number) => void): () => void {
  if (!supabase) {
    onCount(0);
    return () => {};
  }
  const client = supabase;
  const channel = client.channel("online-players", {
    config: { presence: { key: account.id } },
  });
  const recount = (): void => {
    const state = channel.presenceState();
    onCount(Object.keys(state).length);
  };
  channel.on("presence", { event: "sync" }, recount);
  channel.on("presence", { event: "join" }, recount);
  channel.on("presence", { event: "leave" }, recount);
  channel.subscribe((status) => {
    if (status === "SUBSCRIBED") {
      void channel.track({ username: account.username, at: Date.now() });
    }
  });
  return () => {
    void client.removeChannel(channel);
  };
}
